import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  UserCheck,
  Shield,
  MessageSquare,
  FileText,
  Activity,
  Building,
  RefreshCw,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

// Project-style Action Dropdown – Team Collaboration
const ActionDropdown = ({ member, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setOpenUpward(window.innerHeight - rect.bottom < 160);
    }
    setIsOpen((v) => !v);
  };

  return (
    <div ref={dropdownRef} className="relative" style={{ zIndex: isOpen ? 10000 : 'auto' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-[var(--gold-primary)] rounded hover:bg-[#FFF9E6] hover:border-[var(--gold-primary)] transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={() => { setIsOpen(false); onView(member); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Member
              </button>
            </li>
            <li>
              <button
                onClick={() => { setIsOpen(false); onEdit(member); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Member
              </button>
            </li>
            <li className="border-t border-gray-200">
              <button
                onClick={() => { setIsOpen(false); onDelete(member); }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Remove Member
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const TeamCollaboration = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // State management
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    projectManagers: 0,
    collaborators: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });


  // Load team members
  const loadTeamMembers = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString()
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}?${queryParams}`, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        const members = data.data || [];

        const mappedMembers = members.map((member) => {
          const collaborationScore = member.performance?.collaborationScore || 0;
          const lastActive = member.updatedAt
            ? new Date(member.updatedAt).toISOString().split('T')[0]
            : 'N/A';
          const assignedProjects = member.assignedProjects || [];

          return {
            id: member._id || member.id,
            _id: member._id || member.id,
            name: member.name || 'Unknown User',
            email: member.email || '',
            role: member.role || 'Team Member',
            department: member.department || 'Not Specified',
            status: member.status || 'active',
            joinDate: member.joinDate ? new Date(member.joinDate).toISOString().split('T')[0] : 'N/A',
            lastActive,
            phone: member.phone || '',
            location: member.location || '',
            skills: member.skills || [],
            projects: assignedProjects.length,
            tasks: member.performance?.tasksCompleted || 0,
            completedTasks: member.performance?.tasksCompleted || 0,
            collaborationScore,
            avatar: member.avatar || null
          };
        });

        setTeamMembers(mappedMembers);

        // Calculate stats
        const totalMembers = data.pagination?.totalItems || mappedMembers.length;
        const activeMembers = mappedMembers.filter(member => member.status === 'active').length;
        const projectManagers = mappedMembers.filter(member =>
          member.role?.toLowerCase().includes('manager') ||
          member.role?.toLowerCase().includes('project manager')
        ).length;
        const collaborators = totalMembers - projectManagers;

        setStats({
          totalMembers,
          activeMembers,
          projectManagers,
          collaborators
        });

        setPagination(prev => ({
          ...prev,
          totalItems: data.pagination?.totalItems || totalMembers,
          totalPages: data.pagination?.totalPages || Math.ceil(totalMembers / prev.itemsPerPage),
          currentPage: data.pagination?.currentPage || prev.currentPage
        }));
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load team members' }));
        throw new Error(errorData.message || 'Failed to load team members');
      }
    } catch (err) {
      console.error('Error loading team members:', err);
      showError(`Failed to load team members: ${err.message}`);
      // Fallback to empty array on error
      setTeamMembers([]);
      setStats({
        totalMembers: 0,
        activeMembers: 0,
        projectManagers: 0,
        collaborators: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeamMembers();
  }, []);

  // Reload when pagination changes
  useEffect(() => {
    loadTeamMembers();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  const handleAddMember = () => {
    navigate('/projects/team-collaboration/create');
  };

  const handleEditMember = (member) => {
    navigate(`/projects/team-collaboration/edit/${member.id}`);
  };

  const handleViewMember = (member) => {
    navigate(`/projects/team-collaboration/view/${member.id}`);
  };

  const handleDeleteMember = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setIsDeleting(true);
    try {
      const memberId = memberToDelete._id || memberToDelete.id;
      if (!memberId) {
        throw new Error('Member ID not found');
      }

      const response = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}/${memberId}`, {
        method: 'DELETE',
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        success('Team member deleted successfully');
        // Reload team members
        await loadTeamMembers();
        setShowDeleteModal(false);
        setMemberToDelete(null);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete team member' }));
        throw new Error(errorData.message || 'Failed to delete team member');
      }
    } catch (err) {
      console.error('Error deleting team member:', err);
      showError(err.message || 'Failed to delete team member');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = () => {
    loadTeamMembers();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/team-collaboration';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh team members list',
      disabled: loading
    },
    {
      label: 'Add Team Member',
      icon: <UserPlus size={18} />,
      onClick: handleAddMember,
      variant: 'primary',
      tooltip: 'Add a new team member'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': 'success',
      'inactive': 'secondary',
      'pending': 'warning'
    };
    return statusConfig[status] || 'secondary';
  };

  const getRolePill = (role) => {
    const config = {
      'project manager': {
        bg: '#DBEAFE',
        color: '#1D4ED8',
        text: 'Project Manager',
      },
      'senior developer': {
        bg: '#DCFCE7',
        color: '#15803D',
        text: 'Senior Developer',
      },
      'ux designer': {
        bg: '#FEF3C7',
        color: '#B45309',
        text: 'UX Designer',
      },
      'devops engineer': {
        bg: '#E0E7FF',
        color: '#4338CA',
        text: 'DevOps Engineer',
      },
      'business analyst': {
        bg: '#FCE7F3',
        color: '#BE185D',
        text: 'Business Analyst',
      },
      'qa engineer': {
        bg: '#FEE2E2',
        color: '#B91C1C',
        text: 'QA Engineer',
      },
    };

    const key = role?.toLowerCase();
    const r = config[key] || {
      bg: '#F3F4F6',
      color: '#374151',
      text: role || 'Team Member',
    };

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '4px 10px',
          fontSize: '12px',
          fontWeight: 500,
          borderRadius: '999px',
          backgroundColor: r.bg,
          color: r.color,
          whiteSpace: 'nowrap',
          border: `1px solid ${r.color}20`,
          lineHeight: 1.2,
        }}
      >
        {r.text}
      </span>
    );
  };

  const getCollaborationScoreColor = (score) => {
    if (score >= 90) return '#10B981';
    if (score >= 80) return '#F59E0B';
    if (score >= 70) return '#EF4444';
    return '#6B7280';
  };

  const getStatusPill = (status) => {
    const config = {
      active: {
        color: '#10b981',
        bg: '#d1fae5',
        text: 'Active',
      },
      inactive: {
        color: '#6b7280',
        bg: '#f3f4f6',
        text: 'Inactive',
      },
      pending: {
        color: '#f59e0b',
        bg: '#fef3c7',
        text: 'Pending',
      },
    };

    const s = config[status] || config.active;

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          fontSize: '12px',
          fontWeight: 500,
          borderRadius: '999px',
          backgroundColor: s.bg,
          color: s.color,
          whiteSpace: 'nowrap',
          border: `1px solid ${s.color}20`,
        }}
      >
        <span
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: s.color,
          }}
        />
        {s.text}
      </span>
    );
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'name',
      label: 'Team Member',
      sortable: true,
      render: (value, member) => {
        console.log('ROW:', member);
        console.log('VALUE:', value);
        const memberData = member || {};
        return (
          <div className="d-flex align-items-center">
            <div className="avatar-sm me-3">
              {memberData.avatar ? (
                <img src={memberData.avatar} alt={memberData.name} className="rounded-circle" />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center text-white fw-semibold"
                  style={{
                    width: '36px',
                    height: '36px',
                    minWidth: '36px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--gold-primary)',
                    fontSize: '13px',
                    lineHeight: 1,
                    userSelect: 'none',
                  }}
                >
                  {memberData.name
                    ? memberData.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()
                    : 'U'}
                </div>
              )}
            </div>
            <div>
              <div className="fw-semibold">{memberData.name || value || 'Unknown'}</div>
              <div className="text-muted small">{memberData.email || ''}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (value, member) => {
        const role = member?.role || value || 'Team Member';
        return getRolePill(role);
      }
    },
    {
      key: 'department',
      label: 'Department',
      sortable: true,
      render: (value, member) => {
        const department = member?.department || value || 'Not Specified';
        return (
          <div className="d-flex align-items-center">
            <Building size={16} className="text-muted me-2" />
            <span>{department}</span>
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value, member) => {
        const status = member?.status || value || 'active';
        return getStatusPill(status);
      }
    },
    {
      key: 'collaborationScore',
      label: 'Collaboration Score',
      sortable: true,
      render: (value, member) => {
        const score = member?.collaborationScore || value || 0;
        return (
          <div className="d-flex align-items-center">
            <div className="progress me-2" style={{ width: '60px', height: '8px' }}>
              <div
                className="progress-bar"
                style={{
                  width: `${score}%`,
                  backgroundColor: getCollaborationScoreColor(score)
                }}
              />
            </div>
            <span className="fw-semibold" style={{ color: getCollaborationScoreColor(score) }}>
              {score}%
            </span>
          </div>
        );
      }
    },
    {
      key: 'lastActive',
      label: 'Last Active',
      sortable: true,
      render: (value, member) => {
        const lastActive = member?.lastActive || value;
        try {
          if (lastActive && lastActive !== 'N/A') {
            // Handle the date string format 'YYYY-MM-DD'
            const date = new Date(lastActive + 'T00:00:00');
            const isValidDate = !isNaN(date.getTime()) && date.getFullYear() > 1900;

            if (isValidDate) {
              return (
                <div className="d-flex align-items-center">
                  <Clock size={16} className="text-muted me-2" />
                  <span>{date.toLocaleDateString()}</span>
                </div>
              );
            }
          }
          return (
            <div className="d-flex align-items-center">
              <Clock size={16} className="text-muted me-2" />
              <span>N/A</span>
            </div>
          );
        } catch (error) {
          console.error('Date parsing error:', error, lastActive);
          return (
            <div className="d-flex align-items-center">
              <Clock size={16} className="text-muted me-2" />
              <span>N/A</span>
            </div>
          );
        }
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, member) => (
        <ActionDropdown
          member={member}
          onView={handleViewMember}
          onEdit={handleEditMember}
          onDelete={handleDeleteMember}
        />
      )
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Team Collaboration"
        description="Manage team members, roles, and collaboration within projects"
        actions={headerActions}
      />
      <div className="page-container">

        {/* Stats Cards */}
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {[
            {
              icon: Users,
              value: stats.totalMembers,
              label: 'Total Members',
              trend: '+6%',
              up: true,
            },
            {
              icon: UserCheck,
              value: stats.activeMembers,
              label: 'Active Members',
              trend: '+4%',
              up: true,
            },
            {
              icon: Shield,
              value: stats.projectManagers,
              label: 'Project Managers',
              trend: '+2%',
              up: true,
            },
            {
              icon: MessageSquare,
              value: stats.collaborators,
              label: 'Collaborators',
              trend: '-1%',
              up: false,
            },
          ].map(({ icon: Icon, value, label, trend, up }, i) => (
            <div
              key={i}
              className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]"
            >
              {/* Accent line */}
              <div className="h-1 w-full bg-[var(--gold-primary)]" />

              <div className="relative flex items-center gap-3 p-4">
                {/* Trend – EXACT like Dashboard */}
                <div
                  className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${up ? 'text-[#059669]' : 'text-[#dc2626]'
                    }`}
                >
                  {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {trend}
                </div>
                {/* Icon */}
                <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]"><Icon size={20} className="text-[var(--gold-primary)]" />
                </div>
                {/* Text */}
                <div>
                  <div className="text-[20px] font-bold text-[var(--text-primary)]">
                    {value}
                  </div>
                  <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                    {label}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Team Members Table */}
        <DataTable
          data={teamMembers}
          columns={columns}
          title="Team Members"
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          searchable={true}
          emptyMessage="No team members found. Add your first team member to get started."
          emptyIcon={<Users size={48} />}
          showActions={false}
          showCheckboxes={false}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Team Member"
          message={`Are you sure you want to remove "${memberToDelete?.name}" from the team? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          loading={isDeleting}
        />

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default TeamCollaboration;
